import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MapPin, Phone, Clock, Star, ChevronRight, Calendar,
  UtensilsCrossed, ExternalLink, Share2, Heart, Navigation
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';

import { API } from '@/config/api';
import GuestChat from '@/components/chat/GuestChat';
import { openGuestChat } from '@/lib/guestChat';

// Template style configurations
const getTemplateStyles = (template) => {
  if (!template) return {};
  
  const styles = {
    '--primary': template.primary_color,
    '--secondary': template.secondary_color,
    '--accent': template.accent_color,
    '--text': template.text_color,
  };
  
  return styles;
};

// Hero section variants based on template
const HeroSection = ({ restaurant, template, onOrderClick, onReserveClick }) => {
  const heroStyles = {
    'full-image': 'min-h-[60vh] bg-cover bg-center relative',
    'split': 'min-h-[50vh] grid md:grid-cols-2',
    'overlay': 'min-h-[50vh] bg-cover bg-center relative',
  };

  const style = heroStyles[template?.hero_style] || heroStyles['overlay'];
  const isDark = template?.bg_style === 'dark' || template?.bg_style === 'dark-gradient';

  return (
    <div 
      className={style}
      style={{
        backgroundImage: template?.hero_style !== 'split' ? `url(${restaurant.image_url})` : 'none',
        backgroundColor: template?.secondary_color
      }}
    >
      {/* Overlay */}
      {template?.hero_style !== 'split' && (
        <div className="absolute inset-0 bg-black/50" />
      )}
      
      {/* Content */}
      <div className={`relative z-10 flex items-center ${template?.hero_style === 'split' ? '' : 'justify-center text-center'}`}>
        {template?.hero_style === 'split' && (
          <div 
            className="hidden md:block bg-cover bg-center"
            style={{ backgroundImage: `url(${restaurant.image_url})` }}
          />
        )}
        
        <div className={`p-8 md:p-12 ${template?.hero_style === 'split' ? 'flex flex-col justify-center' : ''}`}>
          <h1 
            className={`text-4xl md:text-6xl font-bold mb-4 ${isDark || template?.hero_style !== 'split' ? 'text-white' : ''}`}
            style={{ color: template?.hero_style === 'split' ? template?.text_color : 'white' }}
          >
            {restaurant.name}
          </h1>
          
          <p 
            className={`text-xl mb-2 ${isDark || template?.hero_style !== 'split' ? 'text-white/90' : 'text-gray-600'}`}
          >
            {restaurant.cuisine_type} Cuisine
          </p>
          
          <div className={`flex items-center gap-4 mb-6 ${template?.hero_style !== 'split' ? 'justify-center' : ''}`}>
            <div className="flex items-center gap-1">
              <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
              <span className={isDark || template?.hero_style !== 'split' ? 'text-white' : ''}>{restaurant.rating}</span>
              <span className={isDark || template?.hero_style !== 'split' ? 'text-white/70' : 'text-gray-500'}>
                ({restaurant.review_count} reviews)
              </span>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4 justify-center md:justify-start">
            <Button 
              size="lg" 
              onClick={onOrderClick}
              style={{ backgroundColor: template?.primary_color }}
              className="text-white hover:opacity-90"
            >
              <UtensilsCrossed className="h-5 w-5 mr-2" />
              Order Online
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={onReserveClick}
              className={isDark || template?.hero_style !== 'split' ? 'border-white text-white hover:bg-white/20' : ''}
              style={{ 
                borderColor: template?.hero_style === 'split' ? template?.primary_color : 'white',
                color: template?.hero_style === 'split' ? template?.primary_color : 'white'
              }}
            >
              <Calendar className="h-5 w-5 mr-2" />
              Reserve Table
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Menu Section
const MenuSection = ({ categories, template }) => {
  const [activeCategory, setActiveCategory] = useState(0);
  
  if (!categories || categories.length === 0) return null;
  
  return (
    <section id="menu" className="py-16 px-4" style={{ backgroundColor: template?.secondary_color }}>
      <div className="max-w-6xl mx-auto">
        <h2 
          className="text-3xl md:text-4xl font-bold text-center mb-8"
          style={{ color: template?.text_color }}
        >
          Our Menu
        </h2>
        
        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map((cat, idx) => (
            <Button
              key={cat.id}
              variant={activeCategory === idx ? 'default' : 'outline'}
              onClick={() => setActiveCategory(idx)}
              style={{
                backgroundColor: activeCategory === idx ? template?.primary_color : 'transparent',
                borderColor: template?.primary_color,
                color: activeCategory === idx ? 'white' : template?.primary_color
              }}
            >
              {cat.name}
            </Button>
          ))}
        </div>
        
        {/* Menu Items Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories[activeCategory]?.items?.map(item => (
            <Card key={item.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              {item.image_url && (
                <div 
                  className="h-48 bg-cover bg-center"
                  style={{ backgroundImage: `url(${item.image_url})` }}
                />
              )}
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-lg" style={{ color: template?.text_color }}>
                    {item.name}
                  </h3>
                  <span 
                    className="font-bold"
                    style={{ color: template?.primary_color }}
                  >
                    ${item.price.toFixed(2)}
                  </span>
                </div>
                <p className="text-gray-600 text-sm">{item.description}</p>
                {item.is_popular && (
                  <Badge 
                    className="mt-2"
                    style={{ backgroundColor: template?.accent_color }}
                  >
                    Popular
                  </Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Reviews Section
const ReviewsSection = ({ reviews, template }) => {
  if (!reviews || reviews.length === 0) return null;
  
  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <h2 
          className="text-3xl md:text-4xl font-bold text-center mb-8"
          style={{ color: template?.text_color }}
        >
          What Our Guests Say
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map(review => (
            <Card key={review.id} className="p-6">
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i}
                    className={`h-5 w-5 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                  />
                ))}
              </div>
              <p className="text-gray-600 mb-4">"{review.comment}"</p>
              <p className="text-sm text-gray-400">
                {new Date(review.created_at).toLocaleDateString()}
              </p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Hours & Location Section
const InfoSection = ({ restaurant, hours, template }) => {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  return (
    <section id="info" className="py-16 px-4" style={{ backgroundColor: template?.secondary_color }}>
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Hours */}
          <div>
            <h2 
              className="text-2xl font-bold mb-6 flex items-center gap-2"
              style={{ color: template?.text_color }}
            >
              <Clock className="h-6 w-6" style={{ color: template?.primary_color }} />
              Hours of Operation
            </h2>
            <div className="space-y-3">
              {hours && hours.length > 0 ? (
                hours.map((h, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span style={{ color: template?.text_color }}>{dayNames[h.day_of_week]}</span>
                    <span className="text-gray-600">
                      {h.is_closed ? 'Closed' : `${h.open_time} - ${h.close_time}`}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500">Hours not available</p>
              )}
            </div>
          </div>
          
          {/* Location */}
          <div>
            <h2 
              className="text-2xl font-bold mb-6 flex items-center gap-2"
              style={{ color: template?.text_color }}
            >
              <MapPin className="h-6 w-6" style={{ color: template?.primary_color }} />
              Location
            </h2>
            <p className="text-gray-600 mb-4">{restaurant.address}</p>
            {restaurant.phone && (
              <p className="flex items-center gap-2 text-gray-600 mb-4">
                <Phone className="h-5 w-5" style={{ color: template?.primary_color }} />
                {restaurant.phone}
              </p>
            )}
            <Button 
              variant="outline"
              className="gap-2"
              style={{ borderColor: template?.primary_color, color: template?.primary_color }}
              onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(restaurant.address)}`, '_blank')}
            >
              <Navigation className="h-4 w-4" />
              Get Directions
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Reservation Section
const ReservationSection = ({ restaurant, template }) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [guests, setGuests] = useState(2);
  
  return (
    <section id="reserve" className="py-16 px-4 bg-white">
      <div className="max-w-2xl mx-auto text-center">
        <h2 
          className="text-3xl md:text-4xl font-bold mb-4"
          style={{ color: template?.text_color }}
        >
          Reserve a Table
        </h2>
        <p className="text-gray-600 mb-8">
          Book your dining experience at {restaurant.name}
        </p>
        
        <Card className="p-6">
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium mb-2 text-left">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-left">Time</label>
              <select
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
              >
                <option value="">Select time</option>
                {['11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-left">Guests</label>
              <select
                value={guests}
                onChange={(e) => setGuests(Number(e.target.value))}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
              >
                {[1,2,3,4,5,6,7,8].map(n => (
                  <option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>
                ))}
              </select>
            </div>
          </div>
          
          <Button
            className="w-full text-white"
            size="lg"
            style={{ backgroundColor: template?.primary_color }}
            onClick={() => openGuestChat({ reservation: { party_size: guests, date, time } })}
          >
            Ask for this table
          </Button>
          <p className="text-xs text-gray-500 mt-3">Your request goes straight to the restaurant&apos;s staff in a chat; they confirm the table there.</p>
        </Card>
      </div>
    </section>
  );
};

// Main Landing Page Component
export default function RestaurantLandingPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/landing/r/${slug}`);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Restaurant not found');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Skeleton className="h-[60vh] w-full" />
        <div className="max-w-6xl mx-auto p-8">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid md:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <Skeleton key={i} className="h-64 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Restaurant Not Found</h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <Link to="/">
            <Button>Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { restaurant, template, menu_categories, reviews, operating_hours } = data;

  return (
    <div 
      className="min-h-screen"
      style={getTemplateStyles(template)}
    >
      {/* Sticky Header */}
      <header 
        className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b shadow-sm"
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <h1 
            className="text-xl font-bold"
            style={{ color: template?.primary_color }}
          >
            {restaurant.name}
          </h1>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(restaurant.address)}`, '_blank')}
            >
              <MapPin className="h-4 w-4 mr-1" />
              Directions
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigator.share?.({ title: restaurant.name, url: window.location.href })}
            >
              <Share2 className="h-4 w-4" />
            </Button>
            <Link to={`/restaurant/${restaurant.id}`}>
              <Button 
                size="sm"
                style={{ backgroundColor: template?.primary_color }}
                className="text-white"
              >
                Order Now
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <HeroSection 
        restaurant={restaurant}
        template={template}
        onOrderClick={() => window.location.href = `/restaurant/${restaurant.id}`}
        onReserveClick={() => document.getElementById('reserve')?.scrollIntoView({ behavior: 'smooth' })}
      />

      {/* Menu */}
      <MenuSection categories={menu_categories} template={template} />

      {/* Reviews */}
      <ReviewsSection reviews={reviews} template={template} />

      {/* Info */}
      <InfoSection restaurant={restaurant} hours={operating_hours} template={template} />

      {/* Reservation */}
      <ReservationSection restaurant={restaurant} template={template} />

      {/* Footer */}
      <footer 
        className="py-8 px-4 text-center"
        style={{ backgroundColor: template?.primary_color }}
      >
        <p className="text-white/90">
          © {new Date().getFullYear()} {restaurant.name}. Powered by Chain29.
        </p>
        <div className="flex justify-center gap-4 mt-4">
          <Link to={`/restaurant/${restaurant.id}`} className="text-white hover:underline">
            Order Online
          </Link>
          <span className="text-white/50">|</span>
          <a href="#reserve" className="text-white hover:underline">
            Reservations
          </a>
          <span className="text-white/50">|</span>
          <a href="#menu" className="text-white hover:underline">
            Menu
          </a>
        </div>
      </footer>
      <GuestChat restaurantId={restaurant.id} restaurantName={restaurant.name}
        accent={template?.primary_color || '#F97316'} sourcePage="landing" />
    </div>
  );
}
